import React, { useState } from 'react';
import { Box, Button, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import InlineAlert from '../../../../shared/components/InlineAlert';
import DetailRow from '../../../../shared/components/DetailRow';
import FormActionBar from '../../../../shared/components/FormActionBar';
import { TabPanel } from '../../../../shared/components/TabPanel';
import DatasetStateBadge from './DatasetStateBadge';
import DatasetMisfitsList from './DatasetMisfitsList';
import DatasetConfirmDialog from './DatasetConfirmDialog';
import DatasetSaveTemplateDialog from './DatasetSaveTemplateDialog';
import DatasetRowsView from './DatasetRowsView';
import type { RowEventTarget } from '../../../../types/frontend';
import {
  useDatasetWriteAccess,
  useDeleteEventDataset,
  useEventDatasetStatus,
  useRebuildEventDataset,
} from '../../hooks/useEventDatasets';
import { inputLabel, untranslatedGroups } from '../../utils/datasetMappingUtils';
import type { EventDatasetMisfit, EventDatasetRecord, MisfitCount } from '../../../../types/backend';

interface DatasetStatusPanelProps {
  customerId: string;
  customerName: string;
  dataset: EventDatasetRecord;
  /** Given only to a caller at Admin on the customer, who may edit datasets. */
  onEdit?: () => void;
  /** Tells the operator a delete or rebuild was done or refused. */
  notify: (message: string, severity?: 'success' | 'error') => void;
  /** Opens a row's source event in the Event Explorer. */
  onOpenEvent?: (target: RowEventTarget) => void;
}

interface ColumnGroup {
  column: string;
  total: number;
  reasons: MisfitCount[];
}

function groupByColumn(counts: MisfitCount[]): ColumnGroup[] {
  const groups = new Map<string, ColumnGroup>();
  for (const c of counts) {
    const group = groups.get(c.column) ?? { column: c.column, total: 0, reasons: [] };
    group.total += c.count;
    group.reasons.push(c);
    groups.set(c.column, group);
  }
  return [...groups.values()];
}

const DatasetStatusPanel: React.FC<DatasetStatusPanelProps> = ({
  customerId,
  customerName,
  dataset,
  onEdit,
  notify,
  onOpenEvent,
}) => {
  const { status, loading, error } = useEventDatasetStatus(customerId, dataset.name);
  // The column whose events are listed, and which kind: misfits and untranslated values are read apart.
  const [open, setOpen] = useState<{ column: string; kind: EventDatasetMisfit['kind'] } | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const deletion = useDeleteEventDataset(customerId);
  const [confirmingRebuild, setConfirmingRebuild] = useState(false);
  const rebuild = useRebuildEventDataset(customerId);
  const [savingTemplate, setSavingTemplate] = useState(false);
  // A dataset being deleted takes no more changes: its tables are going.
  const deleting = dataset.status === 'deleting';
  const manageable = useDatasetWriteAccess() && !deleting;
  const editable = !!onEdit && !deleting;
  const [chosenTab, setTab] = useState<'status' | 'rows'>('status');
  // A deleting dataset's rows are not offered: its tables are going (EM-UC-37, *a).
  const tab = deleting ? 'status' : chosenTab;

  let statusBody: React.ReactNode;
  if (error) {
    statusBody = <InlineAlert severity="error">Could not read the status of this dataset: {error}</InlineAlert>;
  } else if (loading || !status) {
    statusBody = <CircularProgress size={24} aria-label="Reading status" />;
  } else {
    const groups = groupByColumn(status.misfit_counts);
    // Values kept as read, which no translation covers: the rows fit, so they are not misfits.
    const kept = untranslatedGroups(status.untranslated_counts ?? [], dataset.inputs.length);
    const named = (r: MisfitCount) => (r.input != null && dataset.inputs.length > 1 ? `${inputLabel(r.input)}, ` : '');
    // Days processed without a history row mean every event was set aside, which is not "nothing yet".
    const processedOnly = !!status.earliest_history && !!status.backfilled_through && !status.newest_event;
    let period = 'No history yet';
    if (status.earliest_history && status.newest_event) period = `${status.earliest_history} to ${status.newest_event}`;
    else if (processedOnly) {
      period = `${status.earliest_history} to ${status.curated_through || status.backfilled_through}`;
    }
    statusBody = (
      <>
        <DetailRow label="Period covered" value={period} labelWidth={160} />
        {processedOnly && groups.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            No rows yet: every event so far was set aside as not fitting
          </Typography>
        )}
        <DetailRow label="Backfilled through" value={status.backfilled_through || 'Not started'} labelWidth={160} />
        <DetailRow label="Curated through" value={status.curated_through || 'Not yet curated today'} labelWidth={160} />
        {status.state === 'behind' && status.newest_matching_in_raw && (
          <DetailRow label="Newest matching event in raw" value={status.newest_matching_in_raw} labelWidth={160} />
        )}
        {status.checked_at && <DetailRow label="Raw checked at" value={status.checked_at} labelWidth={160} />}
        {status.no_matching_since && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No matching events since {status.no_matching_since}
          </Typography>
        )}

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Events that did not fit</Typography>
        {groups.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Every matching event fit the dataset.</Typography>
        ) : (
          groups.map((g) => (
            <Box key={g.column} data-testid={`misfit-group-${g.column}`} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{g.column}</Typography>
                <Typography variant="body2" color="text.secondary">{g.total} in total</Typography>
                <Button size="small" onClick={() => setOpen({ column: g.column, kind: 'misfit' })} aria-label={`Show events for ${g.column}`}>
                  Show events
                </Button>
              </Box>
              {g.reasons.map((r) => (
                <Typography key={`${r.input}:${r.reason}`} variant="body2" sx={{ pl: 2 }}>
                  {named(r)}{r.reason}: {r.count}
                </Typography>
              ))}
            </Box>
          ))
        )}
        {kept.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 3, mb: 0.5 }}>Values kept without a translation</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              These rows fit; the value no translation covers is kept as read.
            </Typography>
            {kept.map((g) => (
              <Box key={g.column} data-testid={`untranslated-group-${g.column}`} sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{g.column}</Typography>
                  <Typography variant="body2" color="text.secondary">{g.total} in total</Typography>
                  <Button
                    size="small"
                    onClick={() => setOpen({ column: g.column, kind: 'untranslated' })}
                    aria-label={`Show values for ${g.column}`}
                  >
                    Show values
                  </Button>
                </Box>
                {g.lines.map((line) => <Typography key={line} variant="body2" sx={{ pl: 2 }}>{line}</Typography>)}
              </Box>
            ))}
          </>
        )}
        {open && (
          <DatasetMisfitsList
            key={`${open.kind}:${open.column}`}
            customerId={customerId}
            datasetName={dataset.name}
            inputCount={dataset.inputs.length}
            column={open.column}
            kind={open.kind}
            onClose={() => setOpen(null)}
          />
        )}
      </>
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }} data-testid="dataset-status-panel">
      <Box sx={{ px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>{dataset.name}</Typography>
        <DatasetStateBadge state={status?.state} loading={loading} error={error} />
        {status?.reason && <Typography variant="body2" color="text.secondary">{status.reason}</Typography>}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={tab} onChange={(_, next: 'status' | 'rows') => setTab(next)}>
          <Tab label="Status" value="status" />
          {!deleting && <Tab label="Rows" value="rows" />}
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <TabPanel value={tab} index="status">
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
              <Box sx={{ maxWidth: 800 }}>
                <DetailRow label="Retention" value={`${dataset.retention_days} days`} labelWidth={160} />
                {statusBody}
              </Box>
            </Box>

            {(manageable || editable) && (
              <FormActionBar
                hideSave
                saving={false}
                size="small"
                onDelete={manageable ? () => setConfirmingDelete(true) : undefined}
                sx={{ px: 3, py: 1.5, mt: 0 }}
              >
                {manageable && (
                  <Button size="small" variant="outlined" onClick={() => setSavingTemplate(true)}>
                    Save as template
                  </Button>
                )}
                {manageable && (
                  <Button size="small" variant="outlined" onClick={() => setConfirmingRebuild(true)}>
                    Rebuild
                  </Button>
                )}
                {editable && <Button size="small" variant="contained" onClick={onEdit}>Edit</Button>}
              </FormActionBar>
            )}
          </Box>
        </TabPanel>
        <TabPanel value={tab} index="rows">
          <DatasetRowsView customerId={customerId} dataset={dataset} onOpenEvent={onOpenEvent} />
        </TabPanel>
      </Box>

      <DatasetConfirmDialog
        open={confirmingDelete}
        title="Delete dataset"
        datasetName={dataset.name}
        confirmText="Delete dataset"
        requireTypedName
        busy={deletion.deleting}
        error={deletion.error}
        onCancel={() => {
          setConfirmingDelete(false);
          deletion.reset();
        }}
        onConfirm={() => deletion.remove(dataset.name, {
          onDone: () => {
            setConfirmingDelete(false);
            notify('Dataset deleted');
          },
          onFailed: (message) => notify(message, 'error'),
        })}
      >
        <Typography>
          Delete the dataset <strong>{dataset.name}</strong> of customer <strong>{customerName}</strong>? Its tables
          and history are dropped. The definition stays listed as deleting until that is done.
        </Typography>
      </DatasetConfirmDialog>
      <DatasetConfirmDialog
        open={confirmingRebuild}
        title="Rebuild dataset"
        datasetName={dataset.name}
        confirmText="Rebuild dataset"
        requireTypedName={false}
        busy={rebuild.rebuilding}
        error={rebuild.error}
        onCancel={() => {
          setConfirmingRebuild(false);
          rebuild.reset();
        }}
        onConfirm={() => rebuild.rebuild(dataset.name, {
          onDone: () => {
            setConfirmingRebuild(false);
            notify('Dataset rebuild started');
          },
          onFailed: (message) => notify(message, 'error'),
        })}
      >
        <Typography>
          Rebuild the dataset <strong>{dataset.name}</strong> of customer <strong>{customerName}</strong>? Its tables
          are dropped and refilled from the raw event archive.
        </Typography>
      </DatasetConfirmDialog>
      {savingTemplate && manageable && (
        <DatasetSaveTemplateDialog
          customerId={customerId}
          dataset={dataset}
          onClose={() => setSavingTemplate(false)}
          notify={notify}
        />
      )}
    </Box>
  );
};

export default DatasetStatusPanel;
